import { useCallback, useRef } from 'react';

const useCanvasInteractions = ({
  droppedItems,
  setDroppedItems,
  setDroppedItemsRealtime,
  commitDroppedItems,
  selectedItem,
  setSelectedItem,
  multiSelectedItems,
  setMultiSelectedItems
}) => {
  const dragThreshold = useRef(5);
  const hasDragged = useRef(false);
  const dragEndTimeout = useRef(null);

  // Handle item selection
  const handleItemClick = useCallback((index, e) => {
    // Always use multi-select behavior for all devices
    e.preventDefault();
    const itemId = droppedItems[index].id;
    
    // Check if item is already selected
    const isAlreadySelected = multiSelectedItems.includes(itemId);
    
    if (isAlreadySelected) {
      // Deselect if clicking the same item again
      setMultiSelectedItems(prev => prev.filter(id => id !== itemId));
    } else {
      // Add to selection
      setMultiSelectedItems(prev => [...prev, itemId]);
    }
    setSelectedItem(null);
  }, [droppedItems, multiSelectedItems, setMultiSelectedItems, setSelectedItem]);

  // Handle canvas background click
  const handlePlaceholderClick = useCallback((e) => {
    // Check if click is on canvas background
    const isCanvasBackground = 
      e.target.className === 'sticky-placeholder' ||
      e.target.className === 'placeholder-image' ||
      e.target.className === 'dropped-items-layer' ||
      e.target.classList?.contains('main-canvas') ||
      e.target.classList?.contains('background-canvas') ||
      e.target.tagName === 'CANVAS';
    
    if (isCanvasBackground) {
      setSelectedItem(null);
      setMultiSelectedItems([]);
    }
  }, [setSelectedItem, setMultiSelectedItems]);

  // Handle real-time item updates
  const handleItemMove = useCallback((updatedItems) => {
    setDroppedItemsRealtime(updatedItems);
    hasDragged.current = true;
  }, [setDroppedItemsRealtime]);

  const handleItemResize = useCallback((updatedItems) => {
    setDroppedItemsRealtime(updatedItems);
    hasDragged.current = true;
  }, [setDroppedItemsRealtime]);

  const handleItemRotate = useCallback((updatedItems) => {
    setDroppedItemsRealtime(updatedItems);
    hasDragged.current = true;
  }, [setDroppedItemsRealtime]);

  // Handle interaction start
  const handleMoveStart = useCallback((e, index) => {
    hasDragged.current = false;
    
    // Select item if not already selected
    if (selectedItem !== index) {
      setSelectedItem(index);
      const newZIndex = Math.max(...droppedItems.map(item => item.zIndex || 0)) + 1;
      setDroppedItems(prevItems =>
        prevItems.map((item, i) => ({
          ...item,
          zIndex: i === index ? newZIndex : (item.zIndex || 1)
        }))
      );
    }
  }, [selectedItem, setSelectedItem, droppedItems, setDroppedItems]);

  const handleResizeStart = useCallback((e, index, resizeType) => {
    hasDragged.current = false;
    
    // Ensure item is selected
    if (selectedItem !== index) {
      setSelectedItem(index);
    }
  }, [selectedItem, setSelectedItem]);

  const handleRotateStart = useCallback((e, index) => {
    hasDragged.current = false;
    
    // Ensure item is selected
    if (selectedItem !== index) {
      setSelectedItem(index);
    }
  }, [selectedItem, setSelectedItem]);

  // Handle interaction end
  const handleMoveEnd = useCallback((index) => {
    commitDroppedItems('move');
    
    // Clear drag flag after a shorter delay for better responsiveness
    if (dragEndTimeout.current) {
      clearTimeout(dragEndTimeout.current);
    }
    
    dragEndTimeout.current = setTimeout(() => {
      hasDragged.current = false;
    }, 50);
  }, [commitDroppedItems]);

  const handleResizeEnd = useCallback((index) => {
    commitDroppedItems('resize');
    
    if (dragEndTimeout.current) {
      clearTimeout(dragEndTimeout.current);
    }
    
    dragEndTimeout.current = setTimeout(() => {
      hasDragged.current = false;
    }, 50);
  }, [commitDroppedItems]);

  const handleRotateEnd = useCallback((index) => {
    commitDroppedItems('rotate');
    
    if (dragEndTimeout.current) {
      clearTimeout(dragEndTimeout.current);
    }
    
    dragEndTimeout.current = setTimeout(() => {
      hasDragged.current = false;
    }, 50);
  }, [commitDroppedItems]);

  // Selection utilities
  const selectAll = useCallback(() => {
    if (droppedItems.length > 0) {
      const allItemIds = droppedItems.map(item => item.id);
      setMultiSelectedItems(allItemIds);
      setSelectedItem(null);
    }
  }, [droppedItems, setMultiSelectedItems, setSelectedItem]);

  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    setMultiSelectedItems([]);
  }, [setSelectedItem, setMultiSelectedItems]);

  const deleteSelected = useCallback(() => {
    if (selectedItem !== null) {
      const newItems = droppedItems.filter((_, index) => index !== selectedItem);
      setDroppedItems(newItems);
      setSelectedItem(null);
    } else if (multiSelectedItems.length > 0) {
      const newItems = droppedItems.filter(item => !multiSelectedItems.includes(item.id));
      setDroppedItems(newItems);
      setMultiSelectedItems([]);
    }
  }, [selectedItem, multiSelectedItems, droppedItems, setDroppedItems, setSelectedItem, setMultiSelectedItems]);

  return {
    // Item interactions
    handleItemClick,
    handlePlaceholderClick,
    
    // Real-time updates
    handleItemMove,
    handleItemResize,
    handleItemRotate,
    
    // Interaction lifecycle
    handleMoveStart,
    handleMoveEnd,
    handleResizeStart,
    handleResizeEnd,
    handleRotateStart,
    handleRotateEnd,
    
    // Selection utilities
    selectAll,
    clearSelection,
    deleteSelected
  };
};

export default useCanvasInteractions;